<template>
  <div class="employee-survey">
    <b-card class="mb-4">
      <b-card-header class="d-flex justify-content-between align-items-center">
        <h2 class="mb-0">Employee Survey</h2>
        <b-badge :variant="getStatusVariant(surveyData.status)">
          {{ surveyData.status }}
        </b-badge>
      </b-card-header>
      <b-card-body>
        <!-- Employee ID Section -->
        <b-form-group label="Employee ID:" class="mb-4">
          <b-form-radio-group v-model="surveyData.empId" :disabled="surveyData.status === 'submitted'" class="mb-2">
            <b-form-radio value="Anonymous">Anonymous</b-form-radio>
            
          </b-form-radio-group>
          <!-- <b-form-input
            v-if="surveyData.empId !== 'Anonymous'"
            v-model="surveyData.empId"
            placeholder="Enter your Employee ID"
            class="mt-2"
            :disabled="surveyData.status === 'submitted'"
          /> -->
        </b-form-group>
      </b-card-body>
    </b-card>

    <b-form @submit.prevent="handleSubmit" v-if="surveyData.status !== 'submitted'">
      <b-row>
        <b-col cols="12" v-for="(sectionKey, sIdx) in orderedSectionKeys" :key="sectionKey">
          <b-card class="mb-4 shadow-sm">
            <b-card-header class="bg-primary">
              <h4 class="mb-0 text-white">{{ sIdx + 1 }}. {{ sections[sectionKey].title }}</h4>
            </b-card-header>
            <b-card-body class="mt-2">
              <b-form-group
                v-for="(question, qIdx) in sections[sectionKey].questions"
                :key="`${sectionKey}-q${qIdx}`"
                :label="`${qIdx + 1}. ${question}`"
                class="mb-4  question-block"
              >
                <b-form-radio-group
                  v-model="responses[sectionKey][`question${qIdx + 1}`].answer"
                  :name="`${sectionKey}-q${qIdx}`"
                  class="mb-2"
                  @change="onAnswerSelected(sectionKey, qIdx)"
                  :disabled="surveyData.status === 'submitted'"
                >
                  <b-form-radio value="Strongly Agree">Strongly Agree</b-form-radio>
                  <b-form-radio value="Agree">Agree</b-form-radio>
                  <b-form-radio value="Neutral">Neutral</b-form-radio>
                  <b-form-radio value="Disagree">Disagree</b-form-radio>
                  <b-form-radio value="Strongly Disagree">Strongly Disagree</b-form-radio>
                </b-form-radio-group>
                <b-form-input
                  v-if="responses[sectionKey][`question${qIdx + 1}`].answer"
                  v-model="responses[sectionKey][`question${qIdx + 1}`].remark"
                  placeholder="Please provide a one-liner reason for your answer"
                  class="mt-2"
                  maxlength="120"
                  :disabled="surveyData.status === 'submitted'"
                />
              </b-form-group>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Final Feedback Section -->
      <b-card class="mb-4 shadow-sm">
        <b-card-header class="bg-light">
          <h4 class="mb-0">11. Final Feedback</h4>
        </b-card-header>
        <b-card-body>
          <b-form-group label="What is one thing Sattva should continue doing?" class="mb-3">
            <b-form-textarea
              v-model="responses.finalFeedback.continueDoing"
              rows="2"
              placeholder="Please provide your feedback..."
            />
          </b-form-group>
          <b-form-group label="What is one thing Sattva should improve or change?" class="mb-3">
            <b-form-textarea
              v-model="responses.finalFeedback.improveChange"
              rows="2"
              placeholder="Please provide your feedback..."
            />
          </b-form-group>
          <b-form-group label="Would you recommend Sattva Human as a great place to work?" class="mb-3">
            <b-form-radio-group v-model="responses.finalFeedback.recommend">
              <b-form-radio value="Yes">Yes</b-form-radio>
              <b-form-radio value="No">No</b-form-radio>
            </b-form-radio-group>
          </b-form-group>
          <b-form-group label="Why?" class="mb-3">
            <b-form-textarea
              v-model="responses.finalFeedback.recommendReason"
              rows="2"
              placeholder="Please explain your reason..."
            />
          </b-form-group>
        </b-card-body>
      </b-card>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-between mb-5">
        <b-button variant="secondary" @click="saveDraft" :disabled="surveyData.status === 'submitted'">
          Save Draft
        </b-button>
        <b-button variant="primary" type="submit" :disabled="surveyData.status === 'submitted'">
          Submit Survey
        </b-button>
        <b-button variant="warning" @click="clearDraft" size="sm">
          Clear Draft
        </b-button>
      </div>
    </b-form>

    <!-- submitted Message -->
    <div v-if="surveyData.status === 'submitted'" class="text-center mt-5">
      <b-alert variant="success" show>
        <h4>Survey submitted Successfully!</h4>
        <p>Thank you for completing the employee survey. Your responses have been recorded.</p>
      </b-alert>
    </div>
  </div>
</template>

<script>

import { BFormRadioGroup, BFormRadio, BFormInput, BFormTextarea, BFormGroup, BCard, BCardHeader, BCardBody, BAlert, BButton, BRow, BCol, BPagination, BForm, BBadge } from 'bootstrap-vue'
import EmployeeSurveyService from '@/services/employee-survey-service'
import { generateResponseId, decodeResponseId } from '@/utils/base64'

export default {
  name: 'EmployeeSurvey',
  data() {
    return {
      surveyData: {
        empId: '',
        status: 'Not Initiated',
      },
      randomId: '',
      // Each question now has { answer, remark }
      responses: {
        teamMembers: this.initSection(4),
        teamLeader: this.initSection(5),
        hod: this.initSection(5),
        managementLeadership: this.initSection(4),
        supportFunctions: this.initSection(6),
        trainingDevelopment: this.initSection(5),
        employeeEngagement: this.initSection(4),
        workCulture: this.initSection(4),
        compensationBenefits: this.initSection(4),
        officeEnvironment: this.initSection(5),
        finalFeedback: {
          continueDoing: '',
          improveChange: '',
          recommend: '',
          recommendReason: '',
        },
      },
      sections: {
        teamMembers: {
          title: 'Team Members',
          questions: [
            'I feel respected and valued by my team members.',
            'There is healthy collaboration and communication in my team.',
            'My team members support me during challenges.',
            'Conflicts in my team are handled professionally.',
          ],
        },
        teamLeader: {
          title: 'Team Leader (Reporting Manager)',
          questions: [
            'My team leader provides clear instructions and expectations.',
            'My team leader is approachable when I need help.',
            'I receive timely and constructive feedback from my team leader.',
            'My work is recognized and appreciated by my team leader.',
            'My team leader supports my career growth.',
          ],
        },
        hod: {
          title: 'Head of Department',
          questions: [
            'The HOD sets a clear direction for the team.',
            'The HOD is available for discussions when needed.',
            'I trust the leadership of my department head.',
            'Department goals are clearly communicated to me.',
            'The HOD fosters a fair and performance-driven environment.',
          ],
        },
        managementLeadership: {
          title: 'Management & Leadership',
          questions: [
            'I trust Sattva\'s leadership and vision for the future.',
            'Sattva\'s leadership communicates key decisions clearly.',
            'Leadership at Sattva is open to employee feedback.',
            'Sattva\'s leadership encourages meritocracy and innovation.',
          ],
        },
        supportFunctions: {
          title: 'Support Functions (HR, IT, Payroll)',
          questions: [
            'The HR team responds to my concerns promptly.',
            'I feel comfortable approaching HR with personal or professional issues.',
            'IT support resolves issues in a timely manner.',
            'The IT systems support my daily work efficiently.',
            'Salaries and reimbursements are processed accurately and on time.',
            'The payroll team is transparent and approachable.',
          ],
        },
        trainingDevelopment: {
          title: 'Training & Development',
          questions: [
            'I have received adequate training to perform my current role.',
            'Learning opportunities are clearly communicated and accessible.',
            'I feel encouraged to improve my skills through training.',
            'The training I receive is relevant and useful for my work.',
            'Sattva invests in my long-term professional growth.',
          ],
        },
        employeeEngagement: {
          title: 'Employee Engagement',
          questions: [
            'I feel emotionally connected to Sattva beyond my daily tasks.',
            'Engagement activities are inclusive and meaningful.',
            'Communication about events and internal updates is effective.',
            'I feel heard when I share suggestions or feedback.',
          ],
        },
        workCulture: {
          title: 'Work Culture',
          questions: [
            'Sattva promotes a healthy work-life balance.',
            'Values like respect and integrity are practiced at Sattva.',
            'Sattva creates a safe space for expressing ideas or concerns.',
            'Sattva embraces diversity and inclusion.',
          ],
        },
        compensationBenefits: {
          title: 'Compensation & Benefits',
          questions: [
            'My compensation is fair based on my responsibilities.',
            'I am satisfied with the benefits and perks I receive.',
            'Performance-based increments are handled transparently.',
            'I understand how my salary is structured.',
          ],
        },
        officeEnvironment: {
          title: 'Office Environment',
          questions: [
            'I am satisfied with the cleanliness and hygiene of the workplace.',
            'The office infrastructure supports my productivity.',
            'Basic facilities (Wi-Fi, power, seating) are consistently functional.',
            'I feel comfortable and safe in my working environment.',
            'The workspace is designed to help me stay focused.',
          ],
        },
      },
      orderedSectionKeys: [
        'teamMembers',
        'teamLeader',
        'hod',
        'managementLeadership',
        'supportFunctions',
        'trainingDevelopment',
        'employeeEngagement',
        'workCulture',
        'compensationBenefits',
        'officeEnvironment',
      ],
    }
  },
  components: {
    BFormRadioGroup, BFormRadio, BFormInput, BFormTextarea, BFormGroup, BCard, BCardHeader, BCardBody, BAlert, BButton, BRow, BCol, BPagination, BForm, BBadge
  },
  methods: {
    initSection(count) {
      // Each question: { answer: '', remark: '' }
      const obj = {}
      for (let i = 1; i <= count; i++) {
        obj[`question${i}`] = { answer: '', remark: '' }
      }
      return obj
    },
    getStatusVariant(status) {
      const variants = {
        'Not Initiated': 'secondary',
        'Initiated': 'info',
        'Draft': 'warning',
        'submitted': 'success',
      }
      return variants[status] || 'secondary'
    },
    saveDraft() {
      this.surveyResponse.status = 'Draft'
      this.surveyResponse.responseId = generateResponseId(this.randomId)

      const draftData = this.surveyResponse
      console.log('Saving draft: ', draftData)
      localStorage.setItem('employeeSurveyDraft', JSON.stringify(draftData))
      this.$bvToast.toast('Draft saved successfully!', {
        title: 'Success',
        variant: 'success',
        solid: true,
      })
    },
    async handleSubmit() {
      // this.saveDraft()
      // console.log('surveyData', this.surveyData)
      this.surveyData.status = 'submitted'
      localStorage.setItem('employeeSurveyResponse', JSON.stringify(this.surveyData))
      const validation = this.validateForm()
      if (!validation.valid) {
        this.$bvToast.toast(validation.message || 'Please fill the complete form', {
          title: 'Validation Error',
          variant: 'danger',
          solid: true,
        })
        return
      }
      
      try {
        this.surveyData.status = 'submitted'
        
        // Generate unique response ID if not already set
        if (!this.surveyData.responseId) {
          this.surveyData.responseId = generateResponseId(this.randomId)
        }
        console.log('Decoded responseId:', decodeResponseId(this.surveyData.responseId))
        const userData = JSON.parse(localStorage.getItem('userData'))
        const isAnonymous = this.surveyData.empId === 'Anonymous'

        if(this.surveyData.empId == 'Anonymous'){
          this.surveyData.empId = 'Anonymous'
        } else {
          this.surveyData.empId = userData?.empId
        }
        
        const response = await EmployeeSurveyService.submitResponse({
          response: this.surveyResponse,
          responseId: this.surveyData.responseId,
          emp_id: isAnonymous ? null : (userData?.empId || this.surveyData.empId),
          isAnonymous: isAnonymous
        })
        
        // Also save to localStorage as backup
        const key = `employeeSurveyResponse_${Date.now()}`
        localStorage.setItem(key, JSON.stringify(this.surveyResponse))
        
        this.$bvToast.toast('Survey submitted successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
        // localStorage.removeItem('employeeSurveyDraft')
      } catch (error) {
        console.error('Error submitting survey:', error)
        this.$bvToast.toast(error.message || 'Failed to submit survey', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    validateForm() {
      for (const sectionKey of Object.keys(this.sections)) {
        if (sectionKey === 'finalFeedback') continue
        const section = this.sections[sectionKey]
        for (let i = 0; i < section.questions.length; i++) {
          const q = this.responses[sectionKey][`question${i + 1}`]
          if (!q.answer || !q.remark) {
            return {
              valid: false,
              message: `Please complete question ${i + 1} in section "${section.title}".`,
            }
          }
        }
      }
      // Final feedback
      const fb = this.responses.finalFeedback
      if (!fb.continueDoing || !fb.improveChange || !fb.recommend || !fb.recommendReason) {
        return {
          valid: false,
          message: 'Please complete all fields in the Final Feedback section.',
        }
      }
      return { valid: true }
    },
    async loadDraft() {
      try {
        // Try to load from localStorage first
        const localDraft = localStorage.getItem('employeeSurveyDraft')
        const localResponse = localStorage.getItem('employeeSurveyResponse')
        console.log('localDraft: ', localDraft)
        console.log('localResponse: ', localResponse)
        if (!localResponse && localDraft) {
          const draft = JSON.parse(localDraft)
          console.log('Parsed draft:', draft)
          
          this.surveyData.empId = draft.empId
          this.surveyData.status = draft.status || 'Draft'
          this.surveyData.responseId = draft.responseId
          
          // Load responses from localStorage
          console.log('Loading draft responses:', draft.responses)
          if (draft.responses) {
            // First, reset all responses to initial state
            this.responses = {
              teamMembers: this.initSection(4),
              teamLeader: this.initSection(5),
              hod: this.initSection(5),
              managementLeadership: this.initSection(4),
              supportFunctions: this.initSection(6),
              trainingDevelopment: this.initSection(5),
              employeeEngagement: this.initSection(4),
              workCulture: this.initSection(4),
              compensationBenefits: this.initSection(4),
              officeEnvironment: this.initSection(5),
              finalFeedback: {
                continueDoing: '',
                improveChange: '',
                recommend: '',
                recommendReason: '',
              },
            }
            
            // Now load the saved responses
            Object.keys(draft.responses).forEach(sectionKey => {
              console.log('Processing section:', sectionKey)
              
              if (sectionKey === 'Final Feedback') {
                console.log('Loading Final Feedback:', draft.responses[sectionKey].responses)
                this.responses.finalFeedback = { ...draft.responses[sectionKey].responses }
              } else {
                // Map section titles to response keys
                const sectionMapping = {
                  'Team Members': 'teamMembers',
                  'Team Leader (Reporting Manager)': 'teamLeader',
                  'Head of Department': 'hod',
                  'Management & Leadership': 'managementLeadership',
                  'Support Functions (HR, IT, Payroll)': 'supportFunctions',
                  'Training & Development': 'trainingDevelopment',
                  'Employee Engagement': 'employeeEngagement',
                  'Work Culture': 'workCulture',
                  'Compensation & Benefits': 'compensationBenefits',
                  'Office Environment': 'officeEnvironment'
                }
                
                const responseKey = sectionMapping[sectionKey]
                if (responseKey && this.responses[responseKey]) {
                  // console.log('Loading section:', sectionKey, '->', responseKey)
                  const questions = draft.responses[sectionKey].responses
                  // console.log('Questions to load:', questions)
                  
                  Object.keys(questions).forEach(qKey => {
                    if (this.responses[responseKey][qKey]) {
                      // console.log('Loading question:', qKey, questions[qKey])
                      this.responses[responseKey][qKey] = { ...questions[qKey] }
                    }
                  })
                }
              }
            })
          }
          console.log('Final loaded responses:', this.responses)
        } else if (localResponse) {

            try {
              console.log('this.surveyResponse.responseId: ', JSON.parse(localResponse).responseId)
            const response = await EmployeeSurveyService.getResponseByResponseId(JSON.parse(localResponse).responseId)
            if (response.success && response.data.response) {
              const backendDraft = response.data.response
              console.log('backendDraft: ', backendDraft)
              if (backendDraft.status === 'submitted') {
                this.surveyData.responseId = backendDraft.responseId
                this.surveyData.empId = backendDraft.isAnonymous ? 'Anonymous' : backendDraft.emp_id
                this.surveyData.status = backendDraft.status
                
                // Load responses from backend
                const backendResponses = backendDraft.response
                Object.keys(backendResponses).forEach(sectionKey => {
                  if (sectionKey === 'Final Feedback') {
                    this.responses.finalFeedback = { ...backendResponses[sectionKey].responses }
                  } else if (this.responses[sectionKey]) {
                    const questions = backendResponses[sectionKey].responses
                    Object.keys(questions).forEach(qKey => {
                      if (this.responses[sectionKey][qKey]) {
                        this.responses[sectionKey][qKey] = { ...questions[qKey] }
                      }
                    })
                  }
                })
              }
            }
          } catch (error) {
            console.log('No backend draft found for user:', userData.empId)
          }
          // const response = localResponse
          // console.log('response with submitted data', response)
          // this.surveyData.empId = response.empId
          // this.surveyData.status = response.status
          // this.surveyData.responseId = response.responseId
          // this.responses = response.responses

        }
        
        // Try to load from backend if user is logged in
        // const userData = JSON.parse(localStorage.getItem('userData'))
        // if (userData?.empId) {
        //   try {
        //     const response = await EmployeeSurveyService.getResponseByEmployeeId()
        //     if (response.success && response.data.response) {
        //       const backendDraft = response.data.response
        //       if (backendDraft.status === 'draft') {
        //         this.surveyData.responseId = backendDraft.responseId
        //         this.surveyData.empId = backendDraft.isAnonymous ? 'Anonymous' : backendDraft.emp_id
        //         this.surveyData.status = 'Draft'
                
        //         // Load responses from backend
        //         const backendResponses = backendDraft.response
        //         Object.keys(backendResponses).forEach(sectionKey => {
        //           if (sectionKey === 'Final Feedback') {
        //             this.responses.finalFeedback = { ...backendResponses[sectionKey].responses }
        //           } else if (this.responses[sectionKey]) {
        //             const questions = backendResponses[sectionKey].responses
        //             Object.keys(questions).forEach(qKey => {
        //               if (this.responses[sectionKey][qKey]) {
        //                 this.responses[sectionKey][qKey] = { ...questions[qKey] }
        //               }
        //             })
        //           }
        //         })
        //       }
        //     }
        //   } catch (error) {
        //     console.log('No backend draft found for user:', userData.empId)
        //   }
        // }
      } catch (error) {
        console.error('Error loading draft:', error)
      }
    },
    onAnswerSelected(sectionKey, qIdx) {
      // Optionally focus the remark input
      this.$nextTick(() => {
        const input = this.$el.querySelector(
          `[name='${sectionKey}-q${qIdx}'] ~ input`
        )
        if (input) input.focus()
      })
    },
    
    clearDraft() {
      localStorage.removeItem('employeeSurveyDraft')
      this.$bvToast.toast('Draft cleared!', {
        title: 'Success',
        variant: 'warning',
        solid: true,
      })
      // Reset form
      this.surveyData.status = 'Initiated'
      this.surveyData.empId = ''
      this.surveyData.responseId = ''
      // Reset responses
      this.responses = {
        teamMembers: this.initSection(4),
        teamLeader: this.initSection(5),
        hod: this.initSection(5),
        managementLeadership: this.initSection(4),
        supportFunctions: this.initSection(6),
        trainingDevelopment: this.initSection(5),
        employeeEngagement: this.initSection(4),
        workCulture: this.initSection(4),
        compensationBenefits: this.initSection(4),
        officeEnvironment: this.initSection(5),
        finalFeedback: {
          continueDoing: '',
          improveChange: '',
          recommend: '',
          recommendReason: '',
        },
      }
    },
  },
  computed: {
    surveyResponse() {
      // Build the JSON structure
      const responses = {}
      for (const sectionKey of Object.keys(this.sections)) {
        if (sectionKey === 'finalFeedback') continue
        const section = this.sections[sectionKey]
        const sectionResponses = {}
        for (let i = 0; i < section.questions.length; i++) {
          sectionResponses[`question${i + 1}`] = {
            question: section.questions[i],
            answer: this.responses[sectionKey][`question${i + 1}`].answer,
            remark: this.responses[sectionKey][`question${i + 1}`].remark,
          }
        }
        responses[section.title] = {
          section: section.title,
          responses: sectionResponses,
        }
      }
      responses['Final Feedback'] = {
        section: 'Final Feedback',
        responses: this.responses.finalFeedback,
      }
      return {
        empId: this.surveyData.empId || 'Anonymous',
        status: this.surveyData.status,
        responses,
      }
    },
  },
  async mounted() {
    if (this.surveyData.status === 'Not Initiated') {
      this.surveyData.status = 'Initiated'
    }
    const userData = JSON.parse(localStorage.getItem('userData'))
    this.randomId = userData?.emp_id
    await this.loadDraft()
  },
}
</script>

<style scoped>
.employee-survey {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px 0 40px 0;
}
.b-card-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}
.question-block {
  /* background: #f7f7fc; */
  border-radius: 8px;
  padding: 18px 18px 10px 18px;
  margin-bottom: 18px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.03);
}
.b-form-radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
}
.b-form-radio {
  margin-bottom: 0.25rem;
}
.b-form-input {
  margin-top: 0.5rem;
}
@media (max-width: 768px) {
  .employee-survey {
    padding: 10px 0;
  }
  .b-card-header h4 {
    font-size: 1.1rem;
  }
  .question-block {
    padding: 12px 8px 8px 8px;
  }
}
</style> 